import { makeApi } from "@zodios/core";
import { z } from "zod";

import { branded } from "@/lib/helpers/branded-helper";
import { subscriberFeedbackAllowedFormDtoSchema } from "@/types/backoffice/dto/subscriber-feedback/subscriber-feedback-allowed-form-dto.ts";
import { subscriberNewPasswordDtoSchema } from "@/types/backoffice/dto/subscriber/subscriber-new-password-dto.ts";

export const subscriberSearchSchema = z.object({
  emailList: z.array(z.any()),
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
  id: z.number().nullable(),
  phoneList: z.array(z.any()),
});

export const findSubscriberByFiltersBody = z.array(
  z.object({
    value: z.string().min(1),
    field: z.enum(["subscriberId", "subscriberLastname", "subscriberFirstname", "email", "phone"]),
    filterType: z.enum(["EQUALS", "CONTAINS", "STARTS_BY", "ENDS_BY"]),
  }),
);

export type TSubscriberSearchSchema = z.infer<typeof subscriberSearchSchema>;

export const backofficeSubscriberApi = makeApi([
  {
    method: "get",
    path: "/feedback/site/:siteId",
    response: z.array(subscriberFeedbackAllowedFormDtoSchema),
    alias: "findAllowedSubscriberFeedbackForms",
  },
  {
    method: "post",
    path: "/search",
    alias: "findSubscriberByFilters",
    response: z.array(subscriberSearchSchema),
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: findSubscriberByFiltersBody,
      },
    ],
  },
  {
    method: "post",
    path: "/",
    response: z.object({ id: branded.subscriberId }).required(),
    alias: "createSubscriber",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          civilityId: z.number(),
          firstname: z.string(),
          lastname: z.string(),
          validMailList: z.array(
            z.object({
              default: z.boolean(),
              error: z.boolean(),
              mail: z.string().email(),
              updated: z.boolean(),
              valid: z.boolean(),
            }),
          ),
          validPhoneList: z.array(
            z.object({
              default: z.boolean(),
              error: z.boolean(),
              phone: z.string(),
              updated: z.boolean(),
              valid: z.boolean(),
            }),
          ),
        }),
      },
    ],
  },
  {
    method: "put",
    path: "/",
    response: z.any(),
    alias: "updateSubscriber",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          civilityId: z.number(),
          firstname: z.string(),
          lastname: z.string(),
          id: z.number(),
          validMailList: z.array(
            z.object({
              default: z.boolean(),
              error: z.boolean(),
              mail: z.string().email(),
              subscriberId: z.number(),
              updated: z.boolean(),
              valid: z.boolean(),
            }),
          ),
          validPhoneList: z.array(
            z.object({
              default: z.boolean(),
              error: z.boolean(),
              phone: z.string(),
              subscriberId: z.number(),
              updated: z.boolean(),
              valid: z.boolean(),
            }),
          ),
        }),
      },
    ],
  },
  {
    method: "post",
    path: "/:id/newPassword",
    alias: "sendSubscriberNewPassword",
    response: subscriberNewPasswordDtoSchema,
  },
  {
    method: "put",
    path: "/:id/privileged/start",
    alias: "setSubscriberPrivileged",
    response: z.any(),
  },
  {
    method: "put",
    path: "/:id/privileged/end",
    alias: "unsetSubscriberPrivileged",
    response: z.any(),
  },
  {
    method: "put",
    path: "/:id/vip/start",
    alias: "setSubscriberVip",
    response: z.any(),
  },
  {
    method: "put",
    path: "/:id/vip/end",
    alias: "unsetSubscriberVip",
    response: z.any(),
  },
]);
